import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

export interface ApiTraceEntry {
  id: string
  method: string
  url: string
  status: number | null
  ok: boolean
  startedAt: string
  finishedAt?: string
  durationMs?: number
  correlationId: string
  requestId?: string | null
  message?: string | null
}

export interface FrontendErrorEntry {
  id: string
  timestamp: string
  route: string
  message: string
  stack?: string | null
  source?: string | null
}

type TrackedConfig = InternalAxiosRequestConfig & {
  metadata?: {
    startedAt?: number
    correlationId?: string
  }
}

const MAX_ENTRIES = 30
const apiTrace: ApiTraceEntry[] = []
const frontendErrors: FrontendErrorEntry[] = []

function makeId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function pushBounded<T>(target: T[], item: T): void {
  target.unshift(item)
  if (target.length > MAX_ENTRIES) target.splice(MAX_ENTRIES)
}

function sanitizeUrl(url: string | undefined): string {
  if (!url) return ''
  try {
    const parsed = new URL(url, window.location.origin)
    for (const key of [...parsed.searchParams.keys()]) {
      if (/token|secret|password|code|auth/i.test(key)) {
        parsed.searchParams.set(key, '[redacted]')
      }
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return String(url).replace(/(token|secret|password|code|auth)=([^&\s]+)/gi, '$1=[redacted]')
  }
}

function stackHead(value: unknown): string | null {
  if (!value) return null
  return String(value).split('\n').slice(0, 8).join('\n').slice(0, 4000)
}

export function prepareDiagnosticsRequest(config: TrackedConfig): TrackedConfig {
  const correlationId = makeId('corr')
  config.metadata = {
    ...(config.metadata || {}),
    startedAt: Date.now(),
    correlationId,
  }
  config.headers = config.headers || {}
  config.headers['X-TicketBuddy-Correlation-Id'] = correlationId
  return config
}

export function recordDiagnosticsResponse(response: AxiosResponse): void {
  const config = response.config as TrackedConfig
  const startedAt = config.metadata?.startedAt || Date.now()
  pushBounded(apiTrace, {
    id: makeId('api'),
    method: String(config.method || 'GET').toUpperCase(),
    url: sanitizeUrl(config.url),
    status: response.status,
    ok: response.status >= 200 && response.status < 400,
    startedAt: new Date(startedAt).toISOString(),
    finishedAt: new Date().toISOString(),
    durationMs: Date.now() - startedAt,
    correlationId: config.metadata?.correlationId || '',
    requestId: response.headers?.['x-ticketbuddy-request-id'] || null,
  })
}

export function recordDiagnosticsError(error: AxiosError): void {
  const config = (error.config || {}) as TrackedConfig
  const startedAt = config.metadata?.startedAt || Date.now()
  pushBounded(apiTrace, {
    id: makeId('api-error'),
    method: String(config.method || 'GET').toUpperCase(),
    url: sanitizeUrl(config.url),
    status: error.response?.status ?? null,
    ok: false,
    startedAt: new Date(startedAt).toISOString(),
    finishedAt: new Date().toISOString(),
    durationMs: Date.now() - startedAt,
    correlationId: config.metadata?.correlationId || '',
    requestId: error.response?.headers?.['x-ticketbuddy-request-id'] || null,
    message: error.message ? error.message.slice(0, 1000) : null,
  })
}

export function registerGlobalDiagnosticsHandlers(): void {
  window.addEventListener('error', (event) => {
    pushBounded(frontendErrors, {
      id: makeId('js-error'),
      timestamp: new Date().toISOString(),
      route: window.location.pathname + window.location.search,
      message: String(event.message || 'Unknown error').slice(0, 1000),
      stack: stackHead(event.error?.stack),
      source: event.filename ? sanitizeUrl(event.filename) : null,
    })
  })
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    pushBounded(frontendErrors, {
      id: makeId('promise-error'),
      timestamp: new Date().toISOString(),
      route: window.location.pathname + window.location.search,
      message: String(reason?.message || reason || 'Unhandled promise rejection').slice(0, 1000),
      stack: stackHead(reason?.stack),
      source: 'unhandledrejection',
    })
  })
}

export function getApiTraceSnapshot(): ApiTraceEntry[] {
  return apiTrace.map((entry) => ({ ...entry }))
}

export function getFrontendErrorSnapshot(): FrontendErrorEntry[] {
  return frontendErrors.map((entry) => ({ ...entry }))
}

export function createCorrelationId(): string {
  return makeId('report')
}
